import { useId, useState } from 'react'
import { UploadImageResponse, uploadImage } from 'app/lib/upload-image'
import { ImagePickerAsset } from 'expo-image-picker'

export type UploadGroupImagesTuple = [
  (options: UploadGroupImagesOptions) => Promise<UploadGroupImagesResponse | null>,
  {
    loading: boolean
    error: string | undefined
  }
]

export interface UploadGroupImagesResponse {
  profileImageId?: string
  coverImageId?: string
}

export interface UploadGroupImagesOptions {
  groupId: string
  profileImage: ImagePickerAsset | null | undefined
  coverImage: ImagePickerAsset | null | undefined
  onCompleted?: (response: UploadGroupImagesResponse) => void
  onError: (error: any) => void
}

export const useUploadGroupImages = (user: { token: string }): UploadGroupImagesTuple => {
  const id = useId()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const uploadGroupImages = async (options: UploadGroupImagesOptions) => {
    try {
      setLoading(true)
      setError(undefined)

      const promises: Promise<UploadImageResponse | null>[] = []

      promises.push(
        options.profileImage
          ? uploadImage({
            image: options.profileImage.uri,
            name: `profile-image-${options.groupId}-${id}`,
            authToken: user.token,
            collection: 'groupImages',
          })
          : Promise.resolve(null)
      )

      promises.push(
        options.coverImage
          ? uploadImage({
            image: options.coverImage.uri,
            name: `cover-image-${options.groupId}-${id}`,
            authToken: user.token,
            collection: 'groupImages',
          })
          : Promise.resolve(null)
      )

      const responses = await Promise.all(promises)
      const result = {
        profileImageId: responses[0]?.doc?.id,
        coverImageId: responses[1]?.doc?.id,
      }

      options?.onCompleted?.(result)

      return result

    } catch (error) {
      setError(error.message)
      options.onError(error)
      return null
    } finally {
      setLoading(false)
    }
  }

  return [
    uploadGroupImages,
    {
      loading,
      error,
    },
  ]
}
